import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, btnPrimary, btnSecondary, InlineError, input, labelCls, Modal } from '../ui/Kit';
import { WORK_AUTH } from './common';
import { RESUME_ACCEPT, uploadBatch } from './uploader';

// "Add candidate" (bundle 4a-2) — from the candidate list or a job: upload one resume (straight to Blob, then read like
// any import) or type the details. Duplicates are matched the same way as an import; adding from a job puts the person
// on that job at "Sourced" (adding never claims ownership — the first logged call / e-mail does).

const EMPTY = { full_name: '', email: '', phone: '', linkedin: '', city: '', state: '', country: '', current_title: '',
  current_company: '', work_authorization: 'unknown', tags: '', source: 'manual' };
const RESULT_TEXT = { created: 'Added as a new candidate', merged: 'Matched an existing candidate — the details were added to them',
  duplicate: 'Already in the database (the same resume)' };

export default function AddCandidateDialog({ open, onClose, requirementId, jobTitle, onAdded }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState('upload');
  const [f, setF] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);           // {result, candidate_id, name, reason}
  const fileRef = useRef(null);
  useEffect(() => { if (open) { setF(EMPTY); setFile(null); setProgress(0); setError(''); setDone(null); setMode('upload'); } }, [open]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const waitFor = async (importId) => {
    for (let n = 0; n < 60; n += 1) {
      const st = await api(`/api/candidate-imports/${importId}`);
      const item = st.items[0];
      if (item && (item.status === 'done' || item.status === 'failed')) return item;
      await new Promise((r) => setTimeout(r, 2000));
    }
    return null;
  };
  const upload = async () => {
    setBusy(true); setError('');
    try {
      const id = await uploadBatch(api, [file], { requirementId, single: true, onFile: (_i, p) => p.progress != null && setProgress(p.progress) });
      const item = await waitFor(id);
      if (!item) setDone({ result: 'queued', reason: 'Still reading the resume — it will appear in the list shortly.' });
      else if (item.status === 'failed') setError(item.reason || 'The resume could not be read');
      else { setDone({ result: item.result, candidate_id: item.candidate_id, name: item.candidate_name, reason: item.reason }); onAdded && onAdded(); }
    } catch (e) { setError(e.message); }
    setBusy(false);
  };
  const manual = async () => {
    setBusy(true); setError('');
    try {
      const body = { ...f, tags: f.tags.split(/[;,]/).map((t) => t.trim()).filter(Boolean), requirement_id: requirementId || null };
      ['email', 'phone', 'linkedin', 'city', 'state', 'country', 'current_title', 'current_company'].forEach((k) => { if (!body[k]) body[k] = null; });
      const d = await api('/api/candidates', { method: 'POST', body });
      setDone({ result: d.result, candidate_id: d.candidate.id, name: d.candidate.full_name, flags: d.flags, suggestions: d.suggestions.length });
      onAdded && onAdded();
    } catch (e) { setError(e.message); }
    setBusy(false);
  };
  const footer = done
    ? <><button className={btnSecondary} onClick={onClose}>Close</button>
      {done.candidate_id && <button className={btnPrimary} onClick={() => navigate(`/candidates/${done.candidate_id}`)}>Open {done.name}</button>}</>
    : <><button className={btnSecondary} onClick={onClose}>Cancel</button>
      <button className={btnPrimary} disabled={busy || (mode === 'upload' ? !file : !f.full_name.trim())} onClick={mode === 'upload' ? upload : manual} data-testid="add-submit">
        {busy ? (mode === 'upload' ? (progress < 100 ? `Uploading ${progress}%` : 'Reading the resume…') : 'Adding…') : 'Add candidate'}</button></>;
  return (
    <Modal open={open} title={jobTitle ? `Add a candidate to ${jobTitle}` : 'Add candidate'} onClose={onClose} footer={footer} width="max-w-xl">
      {done ? (
        <div className="text-sm space-y-2" data-testid="add-done">
          <p className="font-semibold text-[#0F172A]">{RESULT_TEXT[done.result] || done.reason}</p>
          {done.flags > 0 && <p className="text-[#991B1B]">They share a phone, e-mail or resume with a differently named person — flagged for review.</p>}
          {(done.suggestions > 0 || (done.reason || '').includes('Possible duplicate')) && <p className="text-[#92400E]">They may be a duplicate of someone already in the database — see Duplicates.</p>}
          {requirementId && done.candidate_id && <p className="text-[#64748B]">Added to the job at “Sourced”.</p>}
        </div>
      ) : (
        <>
          <div className="flex gap-1 p-1 bg-[#EEF1F6] rounded-xl w-max mb-4" role="tablist" aria-label="How to add">
            {[['upload', 'Upload a resume'], ['manual', 'Type the details']].map(([k, label]) => (
              <button key={k} role="tab" aria-selected={mode === k} onClick={() => setMode(k)}
                className={`px-3.5 py-2 rounded-lg text-sm font-semibold ${mode === k ? 'bg-white text-[#0F172A] shadow-sm' : 'text-[#475569]'}`}>{label}</button>
            ))}
          </div>
          {mode === 'upload' ? (
            <div>
              <button type="button" className={btnSecondary} onClick={() => fileRef.current && fileRef.current.click()}>{file ? 'Choose another file' : 'Choose a resume'}</button>
              <input ref={fileRef} type="file" accept={RESUME_ACCEPT} className="hidden" data-testid="resume-input"
                onChange={(e) => { const x = e.target.files[0]; if (x && x.size > 10 * 1024 * 1024) setError('Up to 10 MB'); else { setFile(x || null); setError(''); } }} />
              {file && <p className="text-sm mt-2">{file.name}</p>}
              <p className="text-xs text-[#64748B] mt-2">PDF, DOCX, DOC or TXT. The details are read from the resume; you can edit them after.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block sm:col-span-2"><span className={labelCls}>Full name *</span><input className={input} value={f.full_name} onChange={set('full_name')} maxLength={255} /></label>
              <label className="block"><span className={labelCls}>E-mail</span><input type="email" className={input} value={f.email} onChange={set('email')} /></label>
              <label className="block"><span className={labelCls}>Phone</span><input className={input} value={f.phone} onChange={set('phone')} /></label>
              <label className="block sm:col-span-2"><span className={labelCls}>LinkedIn</span><input className={input} value={f.linkedin} onChange={set('linkedin')} placeholder="https://www.linkedin.com/in/…" /></label>
              <label className="block"><span className={labelCls}>Current title</span><input className={input} value={f.current_title} onChange={set('current_title')} /></label>
              <label className="block"><span className={labelCls}>Current company</span><input className={input} value={f.current_company} onChange={set('current_company')} /></label>
              <label className="block"><span className={labelCls}>City</span><input className={input} value={f.city} onChange={set('city')} /></label>
              <label className="block"><span className={labelCls}>State</span><input className={input} value={f.state} onChange={set('state')} /></label>
              <label className="block"><span className={labelCls}>Country</span><input className={input} value={f.country} onChange={set('country')} /></label>
              <label className="block"><span className={labelCls}>Work authorization</span>
                <select className={input} value={f.work_authorization} onChange={set('work_authorization')}>{WORK_AUTH.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
              <label className="block"><span className={labelCls}>Source</span>
                <select className={input} value={f.source} onChange={set('source')}>
                  <option value="manual">Added by hand</option><option value="referral">Referral</option><option value="job_board">Job board</option></select></label>
              <label className="block"><span className={labelCls}>Tags</span><input className={input} value={f.tags} onChange={set('tags')} placeholder="java; referral" /></label>
            </div>
          )}
        </>
      )}
      <div className="mt-3"><InlineError>{error}</InlineError></div>
    </Modal>
  );
}
